import { AutoComplete } from 'primereact/autocomplete';
import { Dropdown } from 'primereact/dropdown';
import {
  InputNumber,
  InputNumberValueChangeEvent,
} from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  UseFormSetValue,
} from 'react-hook-form';

import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';

export function FormField<
  T extends FieldValues,
  C extends
    | typeof AutoComplete
    | typeof Calendar
    | typeof InputNumber
    | typeof Dropdown
    | typeof InputText,
>({
  label,
  name,
  control,
  component,
  setValue,
  props,
}: {
  label: string;
  name: Path<T>;
  control: Control<T>;
  component: C;
  setValue?: UseFormSetValue<T>;
  props?: React.ComponentPropsWithoutRef<C>;
}) {
  const Component = component as React.ComponentType<any>;

  return (
    <div className="label-input-group">
      <label htmlFor={name} id={`${name}-label`}>
        {label}
      </label>
      <Controller
        name={name}
        control={control}
        disabled={props?.disabled}
        render={({ field: { ref, ...field }, fieldState }) => {
          const value = field.value ?? '';

          if (Component === Dropdown) {
            return (
              <Dropdown
                id={field.name}
                inputId={field.name}
                aria-labelledby={`${field.name}-label`}
                {...(props as React.ComponentPropsWithoutRef<typeof Dropdown>)}
                {...field}
                inputRef={ref}
                className={fieldState.invalid ? 'p-invalid' : ''}
              />
            );
          }

          if (Component === AutoComplete) {
            return (
              <AutoComplete
                id={field.name}
                inputId={field.name}
                aria-labelledby={`${field.name}-label`}
                {...(props as React.ComponentPropsWithoutRef<
                  typeof AutoComplete
                >)}
                {...field}
                inputRef={ref}
                className={fieldState.invalid ? 'p-invalid' : ''}
                onChange={(e: any) => {
                  field.onChange(e.value);

                  if (!setValue) {
                    return;
                  }

                  switch (field.name) {
                    case 'owner':
                      setValue('ownerId' as Path<T>, e.value?.id);
                      break;
                    case 'packedBy':
                      setValue('packedById' as Path<T>, e.value?.id);
                      break;
                    case 'shipFromSite':
                      setValue('shipFromSiteId' as Path<T>, e.value?.id);
                      break;
                  }
                }}
                onSelect={(e: any) => {
                  field.onChange(e.value);

                  if (!setValue) {
                    return;
                  }

                  switch (field.name) {
                    case 'owner':
                      setValue('ownerId' as Path<T>, e.value?.id);
                      break;
                    case 'packedBy':
                      setValue('packedById' as Path<T>, e.value?.id);
                      break;
                    case 'shipFromSite':
                      setValue('shipFromSiteId' as Path<T>, e.value?.id);
                      break;
                  }
                }}
              />
            );
          }

          if (Component === Calendar) {
            const calendarProps = props as React.ComponentPropsWithoutRef<
              typeof Calendar
            >;
            return (
              <Calendar
                id={field.name}
                inputId={field.name}
                aria-labelledby={`${field.name}-label`}
                {...calendarProps}
                {...field}
                inputRef={ref}
                value={
                  value
                    ? calendarProps.parseDateTime
                      ? calendarProps.parseDateTime(String(value))
                      : new Date(String(value))
                    : null
                }
                className={fieldState.invalid ? 'p-invalid' : ''}
                onChange={(e) => {
                  if (e.value instanceof Date || e.value === null) {
                    field.onChange(
                      e.value instanceof Date
                        ? calendarProps.formatDateTime
                          ? calendarProps.formatDateTime(e.value)
                          : e.value.toISOString()
                        : null,
                    );
                  }
                }}
              />
            );
          }

          if (Component === InputNumber) {
            return (
              <InputNumber
                id={field.name}
                inputId={field.name}
                aria-labelledby={`${field.name}-label`}
                {...(props as React.ComponentPropsWithoutRef<
                  typeof InputNumber
                >)}
                {...field}
                inputRef={ref}
                value={field.value as number}
                onValueChange={(e: InputNumberValueChangeEvent) => {
                  field.onChange(e.value);
                }}
                className={fieldState.invalid ? 'p-invalid' : ''}
              />
            );
          }

          return (
            <Component
              id={field.name}
              aria-labelledby={`${field.name}-label`}
              {...props}
              {...field}
              value={value}
              className={fieldState.invalid ? 'p-invalid' : ''}
            />
          );
        }}
      />
    </div>
  );
}
